<template>
    <v-form ref="form" v-model="valid" data-cy="department-form">
        <v-row dense>
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
            <v-col cols="12" sm="6" md="10" class="align-center">
                <v-row>
                    <v-switch
                        v-model="office.active"
                        color="primary"
                        class="ml-auto mr-4 mb-4"
                        :label="office.active ? $t('active') : $t('inactive')"
                        data-cy="status"
                        hide-details
                        density="compact"
                    />
                </v-row>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="9">
                <v-row dense>
                    <v-col cols="12" md="3" class="text-h6 text--secondary">
                        {{ $t('general_info') }}
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="office.name[activeLangCode]"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="name"
                                    :label="`${$t('name')}*`"
                                    :rules="[requiredRule('name')]"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="office.countryId"
                                    :items="useCountries"
                                    :label="`${$t('country')}*`"
                                    clearable
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="country-id"
                                    :rules="[requiredRule('country')]"
                                    @update:model-value="onCountryChange()"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    ref="cityRef"
                                    v-model="office.cityId"
                                    :items="cities"
                                    :label="`${$t('city')}*`"
                                    :disabled="isCitiesLoading || !cities.length"
                                    clearable
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="city-id"
                                    :rules="[requiredRule('city')]"
                                    @update:model-value="onCityChange()"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="office.locationType"
                                    :items="useLocationTypes"
                                    :label="`${$t('type')}*`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="location-type"
                                    :disabled="isShowType()"
                                    :rules="[requiredRule('type')]"
                                    @update:model-value="onLocationTypeChange()"
                                />
                            </v-col>
                            <v-col v-if="office.locationType === Location.Airport && airports.length" cols="12" md="6">
                                <v-select
                                    v-model="office.airportCode"
                                    :items="airports"
                                    :label="`${$t('airport')}*`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="airport-code"
                                    :disabled="isAirportsLoading"
                                    :rules="[requiredRule('airport')]"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="office.street[activeLangCode]"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="street"
                                    :label="$t('street')"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="office.zip"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="zip"
                                    :label="$t('postal_code')"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="office.latitude"
                                    type="number"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="latitude"
                                    hide-spin-buttons
                                    :label="$t('latitude')"
                                    @update:model-value="flickMap"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="office.longitude"
                                    type="number"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="longitude"
                                    hide-spin-buttons
                                    :label="$t('longitude')"
                                    @update:model-value="flickMap"
                                />
                            </v-col>
                        </v-row>
                        <v-row v-if="office.latitude && office.longitude && isShowMap" dense>
                            <v-col cols="12">
                                <v-card class="mb-4">
                                    <v-card-text>
                                        <map-location
                                            :latitude="Number(office.latitude)"
                                            :longitude="Number(office.longitude)"
                                            :is-show-controls="false"
                                            height="300px"
                                        />
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="3" class="text-h6 text--secondary">
                        {{ $t('contacts') }}
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="office.contactPerson[activeLangCode]"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="contact-person"
                                    :label="$t('contact_person')"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="office.fax"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="fax"
                                    :label="$t('fax')"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="office.hoursOfWork"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="hours-of-work"
                                    :label="$t('hours_of_work')"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="6" md="4">
                                <phone-field
                                    v-model="office.phones[0]"
                                    :label="`${$t('phone')} 1`"
                                    density="compact"
                                    color="primary"
                                    data-cy="phone-1"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <phone-field
                                    v-model="office.phones[1]"
                                    :label="`${$t('phone')} 2`"
                                    density="compact"
                                    color="primary"
                                    data-cy="phone-2"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <phone-field
                                    v-model="office.phones[2]"
                                    :label="`${$t('phone')} 3`"
                                    density="compact"
                                    color="primary"
                                    data-cy="phone-3"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="3" class="text-h6 text--secondary">
                        {{ $t('internal_comments') }}
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="office.comments"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="comments"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn data-cy="save" :disabled="isLoading || !valid" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" :disabled="isLoading" variant="text" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import {clean} from '~/utils/helpers'
    import type {OfficeType} from '~/utils/types/account/offices/types'
    import {initOffice} from '~/utils/types/account/offices/helpers'
    import PhoneField from '~/components/snippets/forms/PhoneField.vue'
    import {useCountries} from '~/composables/form-helpers'
    import {Location, type SelectOptionType} from '~/utils/types/types'
    import MapLocation from '~/components/info/mapLocation.src'
    import {useFlagIconCode} from '~/composables/helpers'

    const props = defineProps<{
        officeId?: number | null
        companyId: number
    }>()

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const valid = ref<boolean>(false)
    const isShowMap = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const office = ref<OfficeType>(initOffice())
    const cities = ref<SelectOptionType[]>([])
    const isCitiesLoading = ref<boolean>(true)
    const isAirportsLoading = ref<boolean>(true)
    const airports = ref<SelectOptionType[]>([])
    const cityRef = ref()

    onMounted(async () => {
        if (props.officeId) await loadOffice()
        if (office.value.longitude && office.value.latitude) isShowMap.value = true
        if (office.value.cityId) {
            await loadCities()
            await loadAirports()
        }
    })

    watch(useCountries, async () => {
        if (office.value.countryId) await loadCities()
    })

    async function loadOffice(): Promise<void> {
        try {
            isLoading.value = true
            const {offices} = await app.$api.offices.get({id: props.officeId, locale: 'all'})
            office.value = Object.assign(initOffice(), offices[0])
        } catch (e) {
            office.value = initOffice()
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const data = clone(office.value)
            clean(data)
            if (data.latitude) data.latitude = Number(data.latitude)
            if (data.longitude) data.longitude = Number(data.longitude)
            if (props.officeId) {
                delete data.companyId
                await app.$api.offices.put({...data, companyId: props.companyId})
            } else {
                await app.$api.offices.post({...data, companyId: props.companyId})
            }
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    async function loadAirports(): Promise<void> {
        try {
            isAirportsLoading.value = true
            const rs = await app.$api.airports.get({
                orderBy: 'ALPHABET',
                locationId: office.value.cityId,
            })

            if (rs.length) {
                airports.value = rs.map(({id, name}) => ({title: name, value: id.toString()}))
            }
        } catch (e) {
            airports.value = []
        } finally {
            isAirportsLoading.value = false
        }
    }

    const onCountryChange = async (): Promise<void> => {
        if (office.value.cityId) cityRef.value.reset()
        office.value.locationType = null
        office.value.airportCode = null
        office.value.countryId ? await loadCities() : (cities.value = [])
    }

    const onCityChange = async (): Promise<void> => {
        office.value.locationType = null
        office.value.airportCode = null
        office.value.cityId ? await loadAirports() : (airports.value = [])
    }

    const onLocationTypeChange = async (): Promise<void> => {
        office.value.airportCode = null
    }

    const useLocales = computed(() => {
        const locales = app.$conf.public.locales
        return locales.map(lang => ({value: lang, title: t(`languages.${lang}`), icon: useFlagIconCode.value(lang)}))
    })

    async function loadCities(): Promise<void> {
        try {
            isCitiesLoading.value = true
            const rs = await app.$api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: office.value.countryId,
            })
            if (rs.cities) {
                cities.value = rs.cities.map(({id, name}) => {
                    return {title: name, value: id}
                })
            }
        } catch (e) {
            cities.value = []
        } finally {
            isCitiesLoading.value = false
        }
    }

    const isShowType = (): boolean => {
        if (office.value.countryId && cities.value.length === 0) return false
        return !office.value.countryId || !office.value.cityId
    }

    const useLocationTypes = computed(() => {
        const locations: Location[] = [Location.Airport, Location.City, Location.Accommodation]
        const defaultLocationTypes = locations.map(value => ({value, title: t(`locationTypes.${value}`)}))

        if (office.value.countryId && cities.value.length === 0 && !airports.value.length) {
            return defaultLocationTypes.filter(location => location.value !== Location.Airport)
        }

        return defaultLocationTypes
    })

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    const flickMap = (): void => {
        isShowMap.value = false
        nextTick().then(() => {
            isShowMap.value = true
        })
    }
</script>
